import { RefObject } from 'react'

import { Box, Checkbox, Table, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import { useMemo } from 'modules/core/hooks/useMemo'
import { employeeUser } from 'modules/core/store/collections/users'

import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import type { TableSelection } from 'modules/employees/pages/EditInBulk/hooks/useTableSelection'
import { HEADER_HEIGHT } from 'modules/employees/pages/EditInBulk/types'
import type { ColumnNames } from 'modules/employees/pages/EditInBulk/vars'
import type Employee from 'modules/employees/store/models/Employee'

interface Column {
  headerCell: string
  key: ColumnNames
  information?: string
  width: number
}

export type ColumnsType = Column[]
interface HeaderProps {
  columns: ColumnsType
  tableSelection: TableSelection
  onAllSelect: (column: ColumnNames, selectedIds: number[]) => void
  employees: Employee[]
  legalEntityColSpan?: number
  tableHeadRef: RefObject<HTMLTableSectionElement> | undefined
}
interface CellProps {
  heading: Column
  index: number
  onAllSelect: (column: ColumnNames, selectedIds: number[]) => void
  employees: Employee[]
  tableSelection: TableSelection
  legalEntityColSpan?: number
}
interface HeaderContentProps {
  heading: Column
  index: number
  isChecked: boolean
  onClick: () => void
  highlighted?: boolean
}

const selectableIds = (column: ColumnNames, employees: Employee[]) => {
  if (column !== 'regular_access_starts_on') return employees.map((e) => e.id)

  return employees
    .filter((e) => {
      const confirmed = employeeUser(e).invited && employeeUser(e).joined
      const notSelectable = confirmed && hasRegularAccess(e.attributes.has_regular_access)

      return !notSelectable
    })
    .map((e) => e.id)
}

const HeaderContent = ({ heading, index, isChecked, onClick, highlighted }: HeaderContentProps) => {
  return (
    <Box flexDirection="row" alignItems="center" width="full" paddingY="s4" gap="s16">
      {(index !== 0 || isChecked) && <Checkbox checked={isChecked} onChange={onClick} />}
      <Box flexDirection="row" gap="s8" alignItems="center" justifyContent="center">
        <Text
          size="200"
          weight={highlighted ? 'semibold' : 'medium'}
          color={highlighted ? 'radicalRed1000' : 'grey800'}
          capitalize
        >
          {heading.headerCell}
        </Text>
        {heading.information && <InformationTooltip information={heading.information} />}
      </Box>
    </Box>
  )
}

const HeaderCell = ({
  heading,
  index,
  onAllSelect,
  employees,
  tableSelection,
  legalEntityColSpan,
}: CellProps) => {
  const selectableEmployeesIds = useMemo(
    () => selectableIds(heading.key, employees),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [heading.key, employees.length]
  )

  const isChecked = tableSelection[heading.key]?.length === selectableEmployeesIds.length

  const columnSpan = heading.key === 'legal_entity_and_employee_group' ? legalEntityColSpan : 1

  return (
    <Table.HeadCell
      width={heading.width}
      key={heading.key}
      sticky={index === 0 ? { position: 'left' } : undefined}
      style={{ borderTop: '1px solid #E5E5E5', height: `${HEADER_HEIGHT}px` }}
      colSpan={columnSpan}
    >
      <HeaderContent
        heading={heading}
        index={index}
        onClick={() => onAllSelect(heading.key, selectableEmployeesIds)}
        isChecked={isChecked}
      />
    </Table.HeadCell>
  )
}

const Header = ({
  columns,
  tableSelection,
  onAllSelect,
  employees,
  legalEntityColSpan,
  tableHeadRef,
}: HeaderProps) => {
  return (
    <Table.Head ref={tableHeadRef}>
      <Table.Row
        style={{
          position: 'absolute',
          top: '0px',
          height: `${HEADER_HEIGHT}px`,
          width: 'max-content',
        }}
      >
        {columns.map((heading, index) =>
          HeaderCell({
            heading,
            index,
            onAllSelect,
            employees,
            tableSelection,
            legalEntityColSpan,
          })
        )}
      </Table.Row>
    </Table.Head>
  )
}

export default observer(Header)
